import api from 'api'
import { NotifierType } from 'components/Notifier'
import { AppContext } from 'contexts/AppContext'
import { Dispatch, SetStateAction, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Routes } from 'routes'
import { VoohCampaign, VoohCampaignPotential } from 'types/campaign'
import { UseVoohCampaignActionProps } from './types'
import { FetchResult } from '@apollo/client'
import { FetchPdfTaskStatusResponse } from '../../api/campaign/types'
import { MutationStateResponse } from '../../types/various'

const useVoohCampaignAction = (): UseVoohCampaignActionProps => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const navigate = useNavigate()

  const acceptVoohCampaign = (
    campaignId: VoohCampaign['id'],
    action: 'activate' | 'publish'
  ): Promise<void> =>
    api.voohCampaign
      .publishVoohCampaign(campaignId)
      .then(res => {
        if (!res.data) {
          return
        }
        const { success } = res.data.publishVoohCampaign

        if (success) {
          addNotification(
            NotifierType.SUCCESS,
            t(`form.campaign.${action === 'activate' ? 'approval' : 'publish'}.success`)
          )
          navigate(Routes.VOOH_CAMPAIGNS.ALL)
        }
      })
      .catch(() => void {})

  const copyVoohCampaign = (
    campaignId: VoohCampaign['id'],
    setIsLoadingCopying: Dispatch<SetStateAction<boolean>>
  ): Promise<void> => {
    setIsLoadingCopying(true)
    return api.voohCampaign
      .copyVoohCampaign(campaignId)
      .then(res => {
        setIsLoadingCopying(false)
        if (!res.data) {
          return
        }
        const { success } = res.data.copyVoohCampaign

        if (success) {
          addNotification(NotifierType.SUCCESS, t('form.campaign.copy.success'))
          navigate(Routes.VOOH_CAMPAIGNS.ALL)
        }
      })
      .catch(() => {
        setIsLoadingCopying(false)
      })
  }

  const removeVoohCampaign = (
    campaignId: VoohCampaign['id'],
    setRemovingCampaign: Dispatch<SetStateAction<boolean>>
  ): Promise<void> => {
    setRemovingCampaign(true)
    return api.voohCampaign
      .destroyVoohCampaign(campaignId)
      .then(res => {
        setRemovingCampaign(true)
        if (!res.data) {
          return
        }
        const { success } = res.data.destroyVoohCampaign

        if (success) {
          addNotification(NotifierType.SUCCESS, t('form.campaign.removal.success'))
          navigate(Routes.VOOH_CAMPAIGNS.ALL)
        }
      })
      .catch(() => {
        setRemovingCampaign(false)
      })
  }

  const stopVoohCampaign = (
    campaignId: VoohCampaign['id'],
    setIsLoadingStop: Dispatch<SetStateAction<boolean>>,
    setRefetchCampaign: Dispatch<SetStateAction<boolean>>
  ): Promise<void> => {
    setIsLoadingStop(true)
    return api.voohCampaign
      .stopVoohCampaign(campaignId)
      .then(res => {
        setIsLoadingStop(false)
        if (!res.data) {
          return
        }
        const { success } = res.data.stopVoohCampaign

        if (success) {
          addNotification(NotifierType.SUCCESS, t('form.campaign.finish.success'))
          setRefetchCampaign(true)
        }
      })
      .catch(() => {
        setIsLoadingStop(false)
      })
  }

  const createVoohCampaignSummaryPdf = (
    voohCampaignUuid: VoohCampaign['uuid']
  ): Promise<
    FetchResult<{
      createVoohCampaignSummaryPdf: {
        task: FetchPdfTaskStatusResponse
      } & MutationStateResponse
    }>
  > => {
    return api.voohCampaign.createVoohCampaignSummaryPdf(voohCampaignUuid)
  }

  const fetchPdfTaskStatus = async (
    jobId: string
  ): Promise<FetchPdfTaskStatusResponse | undefined> => {
    const res = await api.campaign.fetchPdfTaskStatus(jobId)
    return res.data?.task
  }

  const checkVoohCampaignPotential = async (
    campaignId: VoohCampaign['id'],
    clubMediaSource?: VoohCampaign['clubMediaSource']
  ): Promise<VoohCampaignPotential[]> => {
    return api.voohCampaign.getVoohCampaignPotential(campaignId).then(response => {
      const potential = response.data.voohCampaignPotential
      return potential.filter(potential => {
        if (clubMediaSource) {
          return potential.takenClubMinutes > potential.totalClubMinutes
        }
        return potential.takenMoveMinutes > potential.totalMoveMinutes
      })
    })
  }

  return {
    stopVoohCampaign,
    createVoohCampaignSummaryPdf,
    acceptVoohCampaign,
    copyVoohCampaign,
    checkVoohCampaignPotential,
    fetchPdfTaskStatus,
    removeVoohCampaign,
  }
}

export default useVoohCampaignAction
